import { useEffect, useState } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, CheckCircle, AlertTriangle } from 'lucide-react';
import { PostureIssue } from '@/hooks/usePostureDetection';

interface PostureFeedbackProps {
  issues: PostureIssue[];
  overallScore?: number;
  enableVoice?: boolean;
}

export const PostureFeedback = ({ issues, overallScore, enableVoice = true }: PostureFeedbackProps) => {
  const [lastAnnouncedIssue, setLastAnnouncedIssue] = useState<string>('');

  useEffect(() => {
    if (!enableVoice || issues.length === 0) return;

    const highPriorityIssue = issues.find(i => i.severity === 'high');
    const issueToAnnounce = highPriorityIssue || issues[0];

    if (issueToAnnounce && issueToAnnounce.message !== lastAnnouncedIssue) {
      announceIssue(issueToAnnounce.message);
      setLastAnnouncedIssue(issueToAnnounce.message);
    }
  }, [issues, enableVoice, lastAnnouncedIssue]);

  const announceIssue = (message: string) => {
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(message);
      utterance.lang = 'pt-BR';
      utterance.rate = 0.9;
      utterance.pitch = 1.0;
      window.speechSynthesis.speak(utterance);
    }
  };

  const getSeverityIcon = (severity: PostureIssue['severity']) => {
    switch (severity) {
      case 'high':
        return <AlertCircle className="h-4 w-4 text-destructive" />;
      case 'medium':
        return <AlertTriangle className="h-4 w-4 text-warning" />;
      case 'low':
        return <AlertTriangle className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getSeverityVariant = (severity: PostureIssue['severity']): 'default' | 'destructive' => {
    switch (severity) {
      case 'high':
        return 'destructive';
      case 'medium':
      case 'low':
        return 'default';
    }
  };

  if (issues.length === 0 && overallScore && overallScore >= 80) {
    return (
      <Alert className="border-green-500/50 bg-green-500/10">
        <CheckCircle className="h-4 w-4 text-green-500" />
        <AlertDescription className="text-green-700 dark:text-green-300">
          Ótima postura! Continue assim. Score: {overallScore}/100
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-2">
      {overallScore !== undefined && (
        <div className="flex items-center justify-between p-2 bg-muted rounded-lg">
          <span className="text-sm font-medium">Score Geral:</span>
          <Badge variant={overallScore >= 80 ? 'default' : 'destructive'}>
            {overallScore}/100
          </Badge>
        </div>
      )}

      {issues.map((issue, index) => (
        <Alert key={index} variant={getSeverityVariant(issue.severity)} className="py-2">
          <div className="flex items-start gap-2">
            {getSeverityIcon(issue.severity)}
            <AlertDescription className="text-sm flex-1">
              {issue.message}
            </AlertDescription>
          </div>
        </Alert>
      ))}
    </div>
  );
};
